import { z } from 'zod'

const exerciseSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  shortCue: z.string().min(1),
  technique: z.array(z.string()).min(1).max(4),
  mistakes: z.array(z.string()).min(1),
  media: z.string().min(1).optional(),
  staticMedia: z.string().min(1).optional(),
  visualStyle: z.enum(['sequence', 'diagram']).optional(),
  mediaFrames: z.number().int().min(2).max(4).optional(),
  frameLabels: z.array(z.string().min(1)).min(2).max(4).optional(),
  mediaAlt: z.string().min(1),
  mode: z.enum(['reps', 'seconds', 'optional']),
  category: z.enum(['warmup', 'strength', 'mobility', 'cooldown']),
  kneeSensitive: z.boolean().optional(),
  aliases: z.array(z.string().min(1)).optional(),
  goal: z.string().min(1).optional(),
  steps: z.array(z.string().min(1)).min(2).max(5).optional(),
  breathing: z.string().min(1).optional(),
  stopSignal: z.string().min(1).optional(),
  regression: z.string().min(1).optional(),
  progression: z.string().min(1).optional()
})

const plannedExerciseSchema = z.object({
  exerciseId: z.string().min(1),
  sets: z.array(z.number().int().nonnegative()).min(1),
  unit: z.enum(['reps', 'seconds']),
  restSeconds: z.number().int().positive().optional(),
  optional: z.boolean().optional(),
  section: z.enum(['warmup', 'work', 'cooldown']).optional(),
  label: z.string().optional()
})

export const programSchema = z.object({
  schemaVersion: z.union([z.literal(1), z.literal(2)]),
  id: z.string().min(1),
  name: z.string().min(1),
  version: z.number().int().positive(),
  durationDays: z.number().int().min(1).max(365),
  startDate: z.iso.date(),
  principle: z.string().min(1),
  exerciseLibrary: z.array(exerciseSchema).min(1),
  sessions: z.array(z.object({
    id: z.string().min(1),
    kind: z.enum(['A', 'B', 'C', 'MIN', 'REST', 'CHECK', 'RECOVERY']),
    name: z.string().min(1),
    durationMin: z.number().int().positive(),
    subtitle: z.string(),
    format: z.enum(['sets', 'circuit']).optional(),
    rounds: z.number().int().min(2).max(5).optional(),
    exercises: z.array(plannedExerciseSchema).min(1),
    unlock: z.object({ minCompletedPriorSessions: z.number().int().nonnegative(), fallbackSessionId: z.string().min(1) }).optional()
  })).min(1),
  schedule: z.array(z.object({
    day: z.number().int().positive(),
    week: z.number().int().positive(),
    sessionId: z.string().nullable(),
    kind: z.enum(['A', 'B', 'C', 'MIN', 'REST', 'CHECK', 'RECOVERY']),
    note: z.string().optional()
  })).min(1)
}).superRefine((program, ctx) => {
  if (program.schedule.length !== program.durationDays) {
    ctx.addIssue({ code: 'custom', path: ['schedule'], message: 'Количество дней не совпадает с durationDays' })
  }
  const exercises = new Set(program.exerciseLibrary.map((item) => item.id))
  const sessions = new Set(program.sessions.map((item) => item.id))
  program.sessions.forEach((session, si) => session.exercises.forEach((item, ei) => {
    if (!exercises.has(item.exerciseId)) {
      ctx.addIssue({ code: 'custom', path: ['sessions', si, 'exercises', ei, 'exerciseId'], message: `Неизвестное упражнение: ${item.exerciseId}` })
    }
  }))
  program.schedule.forEach((day, index) => {
    if (day.sessionId && !sessions.has(day.sessionId)) {
      ctx.addIssue({ code: 'custom', path: ['schedule', index, 'sessionId'], message: `Неизвестная тренировка: ${day.sessionId}` })
    }
  })
  program.sessions.forEach((session, index) => {
    if (session.unlock && !sessions.has(session.unlock.fallbackSessionId)) {
      ctx.addIssue({ code: 'custom', path: ['sessions', index, 'unlock', 'fallbackSessionId'], message: `Неизвестная fallback-тренировка: ${session.unlock.fallbackSessionId}` })
    }
    if (session.format === 'circuit') {
      const rounds = session.rounds ?? 0
      const work = session.exercises.filter((item) => item.section === 'work')
      if (!rounds || !work.length || work.some((item) => item.sets.length !== rounds)) {
        ctx.addIssue({ code: 'custom', path: ['sessions', index], message: 'В круговой тренировке у каждого упражнения должен быть один результат на круг' })
      }
    }
  })
  program.exerciseLibrary.forEach((exercise, index) => {
    if ((exercise.visualStyle ?? 'sequence') === 'sequence' && !exercise.media) {
      ctx.addIssue({ code: 'custom', path: ['exerciseLibrary', index, 'media'], message: `Для последовательности ${exercise.id} нужен файл изображения` })
    }
    if (exercise.mediaFrames && exercise.frameLabels && exercise.mediaFrames !== exercise.frameLabels.length) {
      ctx.addIssue({ code: 'custom', path: ['exerciseLibrary', index, 'frameLabels'], message: `Количество подписей кадров ${exercise.id} не совпадает с mediaFrames` })
    }
  })
})

export type ProgramInput = z.infer<typeof programSchema>

export function parseProgram(input: unknown) {
  return programSchema.safeParse(input)
}
